import { XIcon, SearchIcon } from "@/assets/icons";
import { useEffect, useRef, useState } from "react";
import Results from "./Results";
import SearchFilter from "./SearchFilter";
import { useEcommerce } from "@/components/EcommerceProvider/EcommerceProvider";
import { NexusProduct } from "@/types/nexus-product";
import { useTranslations } from "next-intl";

const GlobalSearchModal = (props: any) => {
  const { searchModalOpen, setSearchModalOpen, currentFilter } = props;
  const [filterValue, setFilterValue] = useState(currentFilter || "all");
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<NexusProduct[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const t = useTranslations("SearchNav");
  const ecommerce = useEcommerce();

  // Autofocus on search input when modal opens
  useEffect(() => {
    if (searchModalOpen && searchInputRef.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [searchModalOpen]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const term = searchTerm.trim().toLowerCase();
      if (term.length >= 2) {
        const products = ecommerce.products ?? [];
        setResults(
          products.filter(
            (p) =>
              p.hidden !== true &&
              !p.disabled &&
              (p.name.toLowerCase().includes(term) ||
                p.description?.toLowerCase().includes(term) ||
                p.categories?.some((cat) => cat.toLowerCase().includes(term)))
          )
        );
      } else {
        setResults([]);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, ecommerce.products]);

  // handle ClickOutside
  const ref = useRef(null);
  useEffect(() => {
    function handleClickOutside() {
      // @ts-ignore
      if (ref.current && !ref.current.contains(event.target)) {
        setSearchModalOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  });

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  };

  return (
    <>
      {searchModalOpen ? (
        <div
          className={`backdrop-filter-sm visible fixed left-0 top-0 z-99999 flex min-h-screen w-full justify-center bg-[#000]/40 px-4 py-8 sm:px-8`}
        >
          <div
            ref={ref}
            className="shadow-7 relative w-full max-w-4xl scale-100 transform rounded-[15px] bg-white transition-all"
          >
            <button
              onClick={() => setSearchModalOpen(false)}
              className="text-body absolute -right-6 -top-6 z-9999 flex h-11.5 w-11.5 items-center justify-center rounded-full border-2 border-stroke bg-white hover:text-dark"
            >
              <XIcon width={24} height={24} />
            </button>

            <div className="h-auto max-h-[calc(100vh-70px)] overflow-y-auto rounded-b-[15px]">
              <div className="rounded-t-[15px] bg-white p-10 pb-3">
                <form
                  onSubmit={handleSearchSubmit}
                  className="sticky top-0 z-999"
                >
                  <div className="relative">
                    <input
                      ref={searchInputRef}
                      type="search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={t("searchTitle")}
                      className="flex h-[56px] w-full items-center rounded-lg border border-gray-3 pl-12 pr-6 outline-hidden duration-300 focus:border-primary"
                    />
                    <div className="absolute left-0 top-0 w-[56px] h-[56px] flex items-center justify-center p-5">
                      <SearchIcon className="w-5 h-5 text-gray-6" />
                    </div>
                  </div>
                </form>
              </div>

              <SearchFilter
                filterValue={filterValue}
                setFilterValue={setFilterValue}
              />

              <div className="bg-white p-5 sm:p-10 pt-7.5">
                <div className="flex flex-wrap -mx-4">
                  <Results
                    {...props}
                    filterValue={filterValue}
                    setFilterValue={setFilterValue}
                    products={results}
                    searchTerm={searchTerm}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default GlobalSearchModal;
